import { PARENT_FIELD_ID } from '../common/constants';
import type { DraftWorkItem } from '../common/types';
import type { IssueTypeToFieldsConfiguration } from '../controllers/use-ai-create-project-context/types';

/**
 * Resolves parent-child relationships for a flat map of draft work items.
 *
 * - Builds a summary → invocationId lookup map.
 * - Resets all `childInvocationIds` before re-resolving.
 * - Assigns `childInvocationIds` to parent items.
 * - Rewrites `fields.parent` to `createdIssueKey` if the parent has already been created.
 * - When `issueTypeMap` is provided, validates hierarchy levels: items at the same hierarchy
 *   level as their claimed parent are promoted to top level (their `fields.parent` is removed).
 *
 * @returns An updated map and a set of invocation IDs that are children (used to derive top-level items).
 */
export const recalculateHierarchy = ({
	itemsMap,
	issueTypeMap,
}: {
	itemsMap: Record<string, DraftWorkItem>;
	issueTypeMap?: IssueTypeToFieldsConfiguration;
}): { newMap: Record<string, DraftWorkItem>; childInvocationIdsSet: Set<string> } => {
	const items = Object.values(itemsMap);

	// First pass: build summary → invocationId lookup and reset childInvocationIds.
	// We must fully populate the summary map before resolving parent-child relationships,
	// since items have a flat structure (a child may appear before its parent).
	const summaryToInvocationId: Record<string, string> = {};
	const newMap: Record<string, DraftWorkItem> = {};
	items.forEach((item) => {
		summaryToInvocationId[item.summary] = item.invocationId;
		newMap[item.invocationId] = { ...item, childInvocationIds: undefined };
	});

	// Second pass: resolve parent-child relationships using the fully-populated summary map.
	const childInvocationIdsSet = new Set<string>();
	items.forEach((item) => {
		const parentSummary = item.fields[PARENT_FIELD_ID];
		if (typeof parentSummary !== 'string') {
			return;
		}

		const parentInvocationId = summaryToInvocationId[parentSummary];
		if (!parentInvocationId || parentInvocationId === item.invocationId) {
			return;
		}

		const parentItem = newMap[parentInvocationId];

		// Validate hierarchy levels — items at the same level cannot be parent-child.
		// Only applies when issueTypeMap is provided.
		if (issueTypeMap) {
			const childHierarchyLevel = issueTypeMap[item.issueTypeId]?.hierarchyLevel ?? 0;
			const parentHierarchyLevel = issueTypeMap[parentItem.issueTypeId]?.hierarchyLevel ?? 0;
			if (childHierarchyLevel === parentHierarchyLevel) {
				// This is a child work item at the same hierarchy level as parent
				// Remove the parent field and promote it to the top level
				delete newMap[item.invocationId].fields[PARENT_FIELD_ID];
				return;
			}
		}

		// Assign this item as a child of its parent.
		parentItem.childInvocationIds = [...(parentItem.childInvocationIds ?? []), item.invocationId];
		childInvocationIdsSet.add(item.invocationId);

		// If the parent has already been created, rewrite the child's `fields.parent`
		// to the parent's createdIssueKey.
		if (parentItem.createdIssueKey) {
			newMap[item.invocationId] = {
				...newMap[item.invocationId],
				fields: {
					...newMap[item.invocationId].fields,
					[PARENT_FIELD_ID]: parentItem.createdIssueKey,
				},
			};
		}
	});

	return { newMap, childInvocationIdsSet };
};
